import React from 'react'

import {
  BorderStyle,
  Box,
  Color,
  GridDisplay,
  JustifyContent,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

type PortionOption = {
  label: string
  numPortions: number
  isAvailable: boolean
}

const PortionButton = withCitrusTheme('button', (theme) => ({
  alignItems: 'center',
  appearance: 'none',
  backgroundColor: theme.color(Color.ColdGrey_50),
  borderColor: theme.color(Color.ColdGrey_100),
  borderRadius: '999px',
  borderStyle: BorderStyle.Solid,
  borderWidth: theme.border.width(0.5).borderWidth,
  boxSizing: 'border-box',
  color: theme.color(Color.ColdGrey_800),
  cursor: 'pointer',
  display: 'inline-flex',
  flexShrink: 0,
  font: 'inherit',
  height: 'clamp(52px, 15vw, 60px)',
  isolation: 'isolate',
  justifyContent: 'center',
  lineHeight: 1,
  overflow: 'hidden',
  padding: 0,
  position: 'relative',
  width: 'clamp(52px, 15vw, 60px)',

  '&:hover': {
    backgroundColor: theme.color(Color.Secondary_50),
    borderColor: theme.color(Color.Secondary_400),
  },

  '&:focus-visible': {
    backgroundColor: theme.color(Color.Secondary_50),
    borderColor: theme.color(Color.Secondary_400),
    boxShadow: 'none',
    outline: 'none',
  },

  '&[aria-pressed="true"]': {
    backgroundColor: theme.color(Color.Secondary_50),
    borderColor: theme.color(Color.Secondary_400),
  },

  '&[aria-pressed="true"]:hover, &[aria-pressed="true"]:focus-visible': {
    backgroundColor: theme.color(Color.Secondary_100),
    borderColor: theme.color(Color.Secondary_500),
  },

  '&:disabled:not([aria-pressed="true"])': {
    backgroundColor: theme.color(Color.ColdGrey_50),
    borderColor: theme.color(Color.ColdGrey_100),
    color: theme.color(Color.ColdGrey_300),
    cursor: 'not-allowed',
  },

  '&:disabled:not([aria-pressed="true"])::after': {
    backgroundColor: theme.color(Color.ColdGrey_200),
    content: '""',
    height: theme.border.width(0.5).borderWidth,
    left: 0,
    position: 'absolute',
    top: '50%',
    transform: 'rotate(-45deg)',
    transformOrigin: 'center',
    width: '100%',
    zIndex: 0,
  },

  '& span': {
    position: 'relative',
    zIndex: 1,
  },

  '&:disabled:not([aria-pressed="true"]) span': {
    backgroundColor: theme.color(Color.ColdGrey_50),
    borderRadius: '999px',
    padding: '0 2px',
  },
}))

type FlexPortionOptionsProps = {
  portions: PortionOption[]
  selectedNumPortions: number
  onChange: (numPortions: number) => void
}

export const FlexPortionOptions = ({
  portions,
  selectedNumPortions,
  onChange,
}: FlexPortionOptionsProps) => {
  return (
    <Box
      display={GridDisplay.Grid}
      gap={3}
      gridTemplateColumns="repeat(5, minmax(0, 60px))"
      justifyContent={JustifyContent.SpaceBetween}
    >
      {portions.map(({ label, numPortions, isAvailable }) => {
        const isSelected = numPortions === selectedNumPortions
        const isDisabled = !isAvailable && !isSelected

        return (
          <PortionButton
            aria-label={`${label} ${numPortions === 1 ? 'portion' : 'portions'}${
              isAvailable ? '' : ' unavailable'
            }`}
            aria-pressed={isSelected}
            disabled={isDisabled}
            key={numPortions}
            onClick={() => onChange(numPortions)}
            type="button"
          >
            <Text size={2}>{label}</Text>
          </PortionButton>
        )
      })}
    </Box>
  )
}
